import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Popover } from "@mui/material";
import { 
  Character, 
  CharacterSheetInterface, 
  CustomParticipant, 
  RolledValue,
  isCreatureInstance, 
} from "@dndModels";

import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from '@utilities/common_utilities';

import HeartGrayFullBlackOutline from "../../../../assets/img/Heart_gray_full_black_outline.png";
import HeartGrayFullWhiteOutline from "../../../../assets/img/Heart_gray_full_white_outline.png";

import HeartLBlueFullBlackOutline from "../../../../assets/img/Heart_lblue_full_black_outline.png";
import HeartLBlueFullWhiteOutline from "../../../../assets/img/Heart_lblue_full_white_outline.png";
import HeartRedFullBlackOutline from "../../../../assets/img/Heart_red_full_black_outline.png";
import HeartRedFullWhiteOutline from "../../../../assets/img/Heart_red_full_white_outline.png";
import HeartYellowFullBlackOutline from "../../../../assets/img/Heart_yellow_full_black_outline.png";
import HeartYellowFullWhiteOutline from "../../../../assets/img/Heart_yellow_full_white_outline.png";

import StringBox from "@components/input/strings/StringBox";
import CheckBox from "@components/input/CheckBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import ButtonBox from "@components/input/buttons/ButtonBox";
import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";
import Roller from "@dndComponents/other/Roller";
import InfoButton from "@components/input/buttons/InfoButton";
import FlexBox from '@components/layout/FlexBox';

interface Props {
  char: CharacterSheetInterface | CustomParticipant;
  disabled?: boolean;
  view_only?: boolean;
  borderColor?: string;
}

const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export default function HPInput({
  char,
  disabled = false,
  view_only,
  borderColor = "inherit",
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [open, setOpen] = useState(false);
  const [my_current, setMyCurrent] = useState(char.hp.current);
  const [my_max_mod, setMyMaxMod] = useState(char.hp.max_modifier);
  const [my_max_override, setMyMaxOverride] = useState(char.hp.override_max);
  const [my_temp, setMyTemp] = useState(char.hp.temp);
  const [change_hit_points, setChangeHitPoints] = useState(0);
  const [hp_change_key, setHPChangeKey] = useState(0);
  const [ping, setPing] = useState(false);
  const [roll, setRoll] = useState<RolledValue[]>([]);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLDivElement | null>(null);
  const [show_calcs, setShowCalcs] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const resourcesChanged = useCallback(() => {
    console.log("HP");
    // console.log(character.hp);
    setMyCurrent(char.hp.current);
    setMyMaxMod(char.hp.max_modifier);
    setMyMaxOverride(char.hp.override_max);
    setMyTemp(char.hp.temp);
  }, [char, setMyCurrent, setMyMaxMod, setMyMaxOverride, setMyTemp]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      char.subscription_holder.subscribe("Resources", mounted, resourcesChanged);
    }
  }, [char, fetched, setFetched, resourcesChanged]);

  const renderExtras = () => {
    return (
      <ModalOrDrawer
        open={ open } 
        onClose={() => {
          setOpen(false);
        }}>
        { renderHPStuff() }
      </ModalOrDrawer>
    );
  }

  const saveChanges = () => {
    // if (char instanceof TransformInstance && char.character && char.spirit.transform) {
    //   // if (char.spirit.transform.current_hit_points.share) {
    //   //   char.character.hp.current = char.hp.current;
    //   // }
    //   // // if (character.spirit.transform.max_hit_points.share) {
    //   // //   character.character.hp.max = character.hp.max;
    //   // // }
    //   // if (char.spirit.transform.temp_hit_points.share) {
    //   //   char.character.hp.temp = char.hp.temp;
    //   // }
    // }
    debugger;
    char.changed();
    char.subscription_holder.resourcesChanged();
  }

  const applyDamage = (amount: number) => {
    if (my_temp > 0) {
      let new_hp = my_temp - amount;
      if (new_hp < 0) {
        setMyTemp(0);
        char.hp.temp = 0;
        new_hp += my_current;
        new_hp = char.hp.allow_negative ? new_hp : Math.max(new_hp, 0);
        setMyCurrent(new_hp);
        char.hp.current = new_hp;
      } else {
        setMyTemp(new_hp);
        char.hp.temp = new_hp;
      }
    } else if (my_current > 0) {
      const new_hp = char.hp.allow_negative ? my_current - amount : Math.max(my_current - amount, 0);
      setMyCurrent(new_hp);
      char.hp.current = new_hp;
    }
    saveChanges();
    setChangeHitPoints(0);
    setHPChangeKey(common_util.get_time_number());
  }

  const renderHPStuff = () => {
    const my_max = char.hp.max;
    const undying = false; // Some features give the ability to continue going when at 0 hp
    return (
      <FlexBox direction="column" style={{ padding: "10px" }}>
        <FlexBox direction="row">
          <FlexBox direction="column" style={{ width: "33%" }}>
            <FlexBox className="centered header">
              Current { char.hp.called }
            </FlexBox>
            <FlexBox>
              <StringBox 
                name=""
                value={`${my_current}`}
                type="number"
                // revert_key={revert_key}
                onBlur={(changed: string) => {
                  let new_hp = +changed;
                  if (new_hp < 0 && !char.hp.allow_negative) {
                    new_hp = 0;
                  } else {
                    const true_max_hp = my_max_override === null ? (my_max + my_max_mod) : (my_max_override + my_max_mod);
                    if (new_hp > true_max_hp) {
                      new_hp = true_max_hp;
                    }
                  }
                  setMyCurrent(new_hp);
                  char.hp.current = new_hp;
                  saveChanges();
                }}
              />
            </FlexBox>
          </FlexBox>
          <FlexBox direction="column" style={{ width: "34%" }}>
            <FlexBox xs_expand={0} className="centered header">
              Max { char.hp.called }
            </FlexBox>
            <FlexBox className="centered" style={{ fontSize: "20px" }}>
              { my_max }
              <InfoButton
                onClick={() => { setShowCalcs(!show_calcs); }}
              />
            </FlexBox>
          </FlexBox>
          <FlexBox direction="column" style={{ width: "33%" }}>
            <FlexBox className="centered header">
              Temp { char.hp.called }
            </FlexBox>
            <FlexBox>
              <StringBox 
                name=""
                value={`${my_temp}`}
                type="number"
                onBlur={(changed: string) => {
                  let new_hp = +changed;
                  if (new_hp < 0) {
                    new_hp = 0;
                  }
                  setMyTemp(new_hp);
                  char.hp.temp = new_hp;
                  saveChanges();
                }}
              />
            </FlexBox>
          </FlexBox>
        </FlexBox>
        <FlexBox>
          <hr />
        </FlexBox>
        <FlexBox direction="row">
          <FlexBox direction="column" style={{ width: "33%" }}>
            <FlexBox className="header centered">Change</FlexBox>
            <FlexBox>
              <StringBox 
                name=""
                value={`${change_hit_points}`}
                type="number"
                change_time={hp_change_key}
                // log_it
                // revert_key={revert_key}
                onBlur={(changed: string) => {
                  setChangeHitPoints(+changed);
                }}
              />
            </FlexBox>
          </FlexBox>
          <FlexBox direction="column" style={{ width: "67%" }}>
            <FlexBox direction="row">
              <FlexBox>
                <DandDButtonBox
                  name="Heal"
                  style={{ color: "green", borderColor: "green" }}
                  onClick={() => {
                    const max_hp = my_max_override === null ? (my_max + my_max_mod) : (my_max_override + my_max_mod);
                    const new_hp = Math.min(my_current + change_hit_points, max_hp);
                    setMyCurrent(new_hp);
                    char.hp.current = new_hp;
                    saveChanges();
                    setChangeHitPoints(0);
                    setHPChangeKey(common_util.get_time_number());
                  }}
                />
              </FlexBox>
              <FlexBox>
                <DandDButtonBox
                  name="Damage"
                  style={{ color: "red", borderColor: "red" }}
                  onClick={() => {
                    applyDamage(change_hit_points);
                  }}
                />
              </FlexBox>
            </FlexBox>
            <FlexBox>
              <DandDButtonBox
                name="Damage with Resistance"
                style={{ color: "red", borderColor: "red" }}
                onClick={() => {
                  const change = Math.floor(change_hit_points / 2);
                  applyDamage(change);
                }}
              />
            </FlexBox>
          </FlexBox>
        </FlexBox>
        <FlexBox>
          <hr />
        </FlexBox>
        <FlexBox direction="row">
          <FlexBox direction="column" style={{ width: "50%" }}>
            <FlexBox className="centered header">
              Max { char.hp.called } Modifier
            </FlexBox>
            <FlexBox>
              <StringBox 
                name=""
                value={ my_max_mod === 0 ? "" : `${my_max_mod}` }
                type="number"
                onBlur={(changed: string) => {
                  let new_mod = 0;
                  if (changed === "") {
                    new_mod = 0;
                  } else {
                    new_mod = +changed;
                  }
                  const true_max_hp = my_max_override === null ? (my_max + new_mod) : (my_max_override + new_mod);
                  const new_current = my_current > true_max_hp ? true_max_hp : my_current;
                  setMyMaxMod(new_mod);
                  setMyCurrent(new_current);
                  char.hp.current = new_current;
                  char.hp.max_modifier = new_mod;
                  saveChanges();
                }}
              />
            </FlexBox>
          </FlexBox>
          <FlexBox direction="column" style={{ width: "50%" }}>
            <FlexBox className="centered header">
              Override Max { char.hp.called }
            </FlexBox>
            <FlexBox>
              <StringBox 
                name=""
                value={`${my_max_override}`}
                type="number"
                onBlur={(changed: string) => {
                  if (changed === "") {
                    char.hp.override_max = null;
                    if (char.hp.current > char.hp.max) {
                      char.hp.current = char.hp.max;
                    }
                    setMyMaxOverride(null);
                  } else {
                    const new_hp = +changed;
                    if (my_current > new_hp) {
                      setMyCurrent(new_hp);
                      char.hp.current = new_hp;
                    }
                    setMyMaxOverride(new_hp);
                    char.hp.override_max = new_hp;
                  }
                  saveChanges();
                }}
              />
            </FlexBox>
          </FlexBox>
        </FlexBox>
        <FlexBox>
          <hr />
        </FlexBox>
        <FlexBox direction="row">
          { renderMultipliers(0) }
          { renderMultipliers(0.5) }
          { renderMultipliers(2) }
          { renderCalcs() }
        </FlexBox>
        { my_current === 0 && 
          my_temp === 0 && 
          !undying && renderDeathSaves()
        }
      </FlexBox>
    );
  }

  const renderCalcs = () => {
    if (show_calcs) {
      return (
        <FlexBox>
          <FlexBox direction="column">
            <FlexBox className="header">Max HP Calculations</FlexBox>
            { char.hp.calc_steps.map((step, key) => {
              return (
                <FlexBox key={key}>
                  { step }
                </FlexBox>
              );
            })}
          </FlexBox>
        </FlexBox>
      );
    }
    return null;
  }

  const renderMultipliers = (mult: number) => {
    if (char instanceof Character) {
      const multipliers = char.damage_multipliers.filter(o => o.multiplier === mult);
      if (multipliers.length > 0) {
        const label = mult === 0.5 ? "Resistances" : (mult === 0 ? "Immunities" : "Vulnerabilities");
        return (
          <FlexBox>
            <span className="header">{ label }:&nbsp;</span>
            { multipliers.map(o => o.damage_type).join(", ") }
          </FlexBox>
        );
      }
    } else if (isCreatureInstance(char)) {
      const creature = char;
      switch (mult) {
        case 0:
          if (creature.immunities_string.length > 0) {
            return (
              <FlexBox>
                <strong>Immunities:</strong> {creature.immunities_string}
              </FlexBox>
            );
          }
        break;
        case 0.5:
          if (creature.resistances_string.length > 0) {
            return (
              <FlexBox>
                <strong>Resistances:</strong> {creature.resistances_string}
              </FlexBox>
            );
          }
        break;
        case 2:
          if (creature.vulnerabilities_string.length > 0) {
            return (
              <FlexBox>
                <strong>Vulnerabilities:</strong> {creature.vulnerabilities_string}
              </FlexBox>
            );
          }
        break;
      }
    }
    return null;
  }

  const renderDeathSaveExtras = () => {
    return (
      <>
        <Popover
          open={ popoverAnchorEl !== null }
          anchorEl={popoverAnchorEl}
          onClose={() => {
            setPopoverAnchorEl(null);
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          { renderRoll() }
        </Popover>
      </>
    );
  }

  const renderRoll = () => {
    if (popoverAnchorEl !== null) {
      return (
        <Roller 
          name="Death Save"
          rolled_values={roll} 
          type="Death Save" 
        />
      );
    }
    return null;
  }

  const renderDeathSaves = () => {
    const fails: any[] = [];
    const successes: any[] = [];
    for (let i = 0; i < 3; ++i) {
      fails.push(
        <CheckBox key={i}
          size={48}
          name=""
          value={char.hp.death_save_fails > i}
          onChange={(changed: boolean) => {
            let new_saves = char.hp.death_save_fails;
            if (changed) {
              new_saves++;
            } else {
              new_saves--;
            }
            char.hp.death_save_fails = new_saves;
            saveChanges();
            setPing(!ping);
          }}
        />
      );
      successes.push(
        <CheckBox key={i}
          size={48}
          name=""
          value={char.hp.death_save_successes > i}
          onChange={(changed: boolean) => {
            let new_saves = char.hp.death_save_successes;
            if (changed) {
              new_saves++;
            } else {
              new_saves--;
            }
            char.hp.death_save_successes = new_saves;
            saveChanges();
            setPing(!ping);
          }}
        />
      );
    }
    // eslint-disable-next-line
    const { advantage, advantages, greater_advantage } = char instanceof Character ? char_util.getAdvantage(char, "Saving Throw", "Death Saves") : { advantage: 0, advantages: [], greater_advantage: [] };
    return (
      <>
        <FlexBox><hr /></FlexBox>
        <FlexBox direction="column">
          <FlexBox className="header centered">
            Death Saves
          </FlexBox>
          <FlexBox direction="row">
            <FlexBox className="centered">
              Failures
            </FlexBox>
            <FlexBox className="centered">
              Successes
            </FlexBox>
          </FlexBox>
          <FlexBox direction="row">
            <FlexBox className="centered">
              { fails }
            </FlexBox>
            <FlexBox className="centered">
              { successes }
            </FlexBox>
          </FlexBox>
          { char instanceof Character && 
            <FlexBox>
              <DandDButtonBox
                name={ `Make a Death Save` }
                advantage={advantage}
                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                  setRoll(char_util.rollD20({ char: char, type: "Death Save", advantage }));
                  setPopoverAnchorEl(event.currentTarget);
                }}
              />
              { renderDeathSaveExtras() }
            </FlexBox>
          }
        </FlexBox>
      </>
    );
  }

  const getHeartImage = () => {
    const max = char.hp.true_max;
    const current = my_current;
    // Blue = Full
    // Yellow = Injured but not bloodied
    // Red = Bloodied
    // Gray = Down
    const color = current === max ? "lblue" : 
      (current > 0.5 * max ? "yellow" : 
      (current > 0 ? "red" : "gray"));
    const outline = borderColor;
    switch(color) {
      case "gray": 
        if (outline === "white") {
          return HeartGrayFullWhiteOutline;
        } else {
          return HeartGrayFullBlackOutline;
        }
      case "lblue": 
        if (outline === "white") {
          return HeartLBlueFullWhiteOutline;
        } else {
          return HeartLBlueFullBlackOutline;
        }
      case "red": 
        if (outline === "white") {
          return HeartRedFullWhiteOutline;
        } else {
          return HeartRedFullBlackOutline;
        }
      case "yellow": 
        if (outline === "white") {
          return HeartYellowFullWhiteOutline;
        } else {
          return HeartYellowFullBlackOutline;
        }
    }
  }

  const renderCurrent = () => {
    const my_max = char.hp.max;
    // console.log(my_max);
    // console.log(my_current);
    return (
      <FlexBox style={{ float: "left", marginLeft: "4px", marginRight: "4px" }}>
        { my_current } 
        { my_temp > 0 && <span style={{ color: "blue" }}>+{my_temp}</span> } 
        &nbsp;/&nbsp;
        { my_max_override === null ? 
          my_max 
        : my_max_override > my_max ?
          <span style={{ color: "green" }}>{ my_max_override }</span> 
        : 
          <span style={{ color: "red" }}>{ my_max_override }</span> 
        } 
        { my_max_mod > 0 ? <span style={{ color: "green" }}>+{my_max_mod}</span> : (my_max_mod < 0 && <span style={{ color: "red" }}>{my_max_mod}</span>) }
      </FlexBox>
    );
  }

  // console.log(char.hp);
  if (disabled) {
    return (
      <img src={ getHeartImage() } alt="heart" 
        style={{
          width: "30px",
          height: "30px",
        }}
      />
    );
  } else {
    // const my_max = char.hp.max;
    const style = borderColor === "inherit" ? { border: `1px solid`} : { border: `1px solid ${borderColor}`};
    return (
      <>
        <FlexBox className={ view_only ? "" : "clickable" } 
          style={style} 
          onClick={() => {
            if (!view_only) {
              setOpen(true);
            }
          }}>
          <FlexBox direction="column" className="centered">
            <FlexBox style={{ 
              fontSize: "10px", 
              height: "16px", 
              margin: "1px",
            }}>
              Hit Points
            </FlexBox>
            <FlexBox className={ view_only ? "centered" : "centered clickable" } style={{
              margin: "1px", 
              fontSize: "16px"
            }}>
              { renderCurrent() }
            </FlexBox>
          </FlexBox>
        </FlexBox>
        { renderExtras() }
      </>
    );
  }
}
